import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError, apiFetch } from '../../data/api';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/TextField';
import { authClient } from './authClient';
import { AuthLayout, FormError } from './AuthLayout';
import { COPY, describeAuthError, describeCallbackError, validatePassword } from './authErrors';
import { GoogleButton } from './GoogleButton';
import { useSession } from './session';

type Invite =
  | { phase: 'loading' }
  | { phase: 'invalid' }
  | { phase: 'offline' }
  | { phase: 'ready'; email: string };

/** Google came back refused while the invite itself is fine: most likely a different Google account. */
function callbackErrorText(code: string | null, invitedEmail: string): string | null {
  if (code?.toUpperCase() === 'INVALID_INVITE')
    return `Google sign-in was refused. Use the Google account for ${invitedEmail}, or create a password below.`;
  return describeCallbackError(code, 'sign-up');
}

/**
 * /invite/:token, the only way to sign up. Loading the invite prefills the email and sets the
 * short-lived tahi_invite cookie that a Google sign-up presents at the OAuth callback, so the
 * Google button only shows once the invite has loaded.
 */
export function InviteScreen() {
  const { token = '' } = useParams();
  const session = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [invite, setInvite] = useState<Invite>({ phase: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const callbackError = params.get('error');

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ email: string }>(`/api/invites/${encodeURIComponent(token)}`)
      .then(({ email }) => {
        if (cancelled) return;
        setInvite({ phase: 'ready', email });
        setError(callbackErrorText(callbackError, email));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setInvite({ phase: err instanceof ApiError ? 'invalid' : 'offline' });
      });
    return () => {
      cancelled = true;
    };
  }, [token, attempt, callbackError]);

  if (session.status === 'signed_in') return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (invite.phase !== 'ready') return;
    setError(null);
    const weak = validatePassword(password);
    if (weak) {
      setError(weak);
      return;
    }
    if (!navigator.onLine) {
      setError(COPY.offlineTryAgain);
      return;
    }
    setBusy(true);
    try {
      const { email } = invite;
      // inviteToken isn't in Better Auth's body type; the Worker's before-hook reads it, and a
      // variable (not an inline literal) skips TypeScript's excess-property check.
      const body = { name: email.split('@')[0] || email, email, password, inviteToken: token };
      const { error: err } = await authClient.signUp.email(body);
      if (err) {
        setError(describeAuthError(err, 'sign-up'));
        return;
      }
      await session.refresh();
      navigate('/', { replace: true });
    } catch {
      setError(COPY.offlineTryAgain);
    } finally {
      setBusy(false);
    }
  }

  if (invite.phase === 'loading') {
    return (
      <AuthLayout title="Join Tahi">
        <p className="text-muted">Checking your invite.</p>
      </AuthLayout>
    );
  }
  if (invite.phase === 'invalid') {
    return (
      <AuthLayout title="Join Tahi">
        <p>{COPY.invalidInvite}</p>
      </AuthLayout>
    );
  }
  if (invite.phase === 'offline') {
    return (
      <AuthLayout title="Join Tahi">
        <p>{COPY.offlineTryAgain}</p>
        <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Join Tahi">
      <p className="text-sm text-muted">
        You were invited with this email. Set a password, or continue with Google.
      </p>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField label="Email" type="email" autoComplete="email" readOnly value={invite.email} />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="At least 10 characters."
          required
          maxLength={128}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <FormError message={error} />
        <Button type="submit" variant="primary" block disabled={busy}>
          Create account
        </Button>
      </form>
      <GoogleButton errorPath={`/invite/${encodeURIComponent(token)}`} />
    </AuthLayout>
  );
}
